import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/AuthContext";
import { Plus, Search, X, Trash2, Edit2, Calendar, FileText } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const empty = {
  first_name: "", last_name: "", date_of_birth: "", gender: "",
  phone: "", email: "", diagnosis: "", notes: ""
};

export default function PatientsList() {
  const { t } = useAuth();
  const [params] = useSearchParams();
  const [allPatients, setAllPatients] = useState([]);
  const [results, setResults] = useState({ patients: null, sessions: [], counts: {} });
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [searching, setSearching] = useState(false);
  const [showForm, setShowForm] = useState(params.get("new") === "1");
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadAll = () => axios.get(`${API}/patients`).then((r) => setAllPatients(r.data)).catch(() => {});
  useEffect(() => { loadAll(); }, []);

  const filtersActive = q || dateFrom || dateTo;

  // Debounced search
  useEffect(() => {
    if (!filtersActive) {
      setResults({ patients: null, sessions: [], counts: {} });
      return;
    }
    setSearching(true);
    const handle = setTimeout(async () => {
      try {
        const url = new URL(`${API}/search`);
        if (q) url.searchParams.set("q", q);
        if (dateFrom) url.searchParams.set("date_from", dateFrom);
        if (dateTo) url.searchParams.set("date_to", dateTo);
        const r = await axios.get(url.toString());
        setResults({
          patients: r.data.patients,
          sessions: r.data.sessions || [],
          counts: r.data.session_counts || {},
        });
      } catch {
        setResults({ patients: [], sessions: [], counts: {} });
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [q, dateFrom, dateTo, filtersActive]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) await axios.put(`${API}/patients/${editing}`, form);
      else await axios.post(`${API}/patients`, form);
      setShowForm(false); setForm(empty); setEditing(null);
      loadAll();
    } catch (e) { alert("Error: " + (e.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  const startEdit = (p) => {
    setEditing(p.patient_id);
    setForm({
      first_name: p.first_name, last_name: p.last_name,
      date_of_birth: p.date_of_birth || "", gender: p.gender || "",
      phone: p.phone || "", email: p.email || "",
      diagnosis: p.diagnosis || "", notes: p.notes || ""
    });
    setShowForm(true);
  };

  const remove = async (p) => {
    if (!window.confirm(t.patients.confirmDelete)) return;
    await axios.delete(`${API}/patients/${p.patient_id}`);
    loadAll();
  };

  const clearFilters = () => { setQ(""); setDateFrom(""); setDateTo(""); };

  const displayedPatients = filtersActive ? (results.patients || []) : allPatients;

  // Sessions matching, grouped by patient
  const matchingSessionsByPatient = useMemo(() => {
    const map = {};
    if (filtersActive && results.sessions) {
      results.sessions.forEach((s) => {
        if (!map[s.patient_id]) map[s.patient_id] = [];
        map[s.patient_id].push(s);
      });
    }
    return map;
  }, [filtersActive, results.sessions]);

  const patientName = (id) => {
    const p = allPatients.find((x) => x.patient_id === id);
    return p ? `${p.first_name} ${p.last_name}` : id;
  };

  return (
    <div className="space-y-6" data-testid="patients-list">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-pine">{t.nav.patients}</div>
          <h1 className="mt-2 font-heading text-3xl tracking-tight">{t.patients.title}</h1>
        </div>
        <button
          onClick={() => { setEditing(null); setForm(empty); setShowForm(true); }}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-pine text-bone text-sm font-medium hover:bg-pine-hover transition"
          data-testid="add-patient-btn"
        >
          <Plus className="w-4 h-4" /> {t.patients.add}
        </button>
      </div>

      {/* Filter bar */}
      <div className="bg-surface border border-hairline rounded-2xl p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[240px]">
          <label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-ink-muted">{t.patients.search}</label>
          <div className="relative mt-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-muted" />
            <input
              value={q} onChange={(e) => setQ(e.target.value)}
              className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-bone border border-hairline focus:outline-none focus:border-pine text-sm"
              data-testid="patient-search"
            />
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-ink-muted">{t.patients.dateFrom}</label>
          <div className="relative mt-1">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-muted pointer-events-none" />
            <input
              type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              className="pl-9 pr-3 py-2.5 rounded-xl bg-bone border border-hairline focus:outline-none focus:border-pine text-sm"
              data-testid="filter-date-from"
            />
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-ink-muted">{t.patients.dateTo}</label>
          <div className="relative mt-1">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-muted pointer-events-none" />
            <input
              type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              className="pl-9 pr-3 py-2.5 rounded-xl bg-bone border border-hairline focus:outline-none focus:border-pine text-sm"
              data-testid="filter-date-to"
            />
          </div>
        </div>
        {filtersActive && (
          <button
            onClick={clearFilters}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm text-ink-muted hover:bg-pine-50 hover:text-ink transition"
            data-testid="clear-filters-btn"
          >
            <X className="w-3.5 h-3.5" /> {t.patients.clearFilters}
          </button>
        )}
      </div>

      {filtersActive && (
        <div className="text-xs text-ink-muted">
          {searching ? t.common.loading : `${displayedPatients.length} · ${(results.sessions || []).length} ${t.patients.sessionMatches}`}
        </div>
      )}

      <div className="bg-surface border border-hairline rounded-2xl overflow-hidden">
        {displayedPatients.length === 0 && !searching ? (
          <div className="py-16 text-center text-ink-muted">{t.patients.empty}</div>
        ) : (
          <div className="divide-y divide-hairline">
            {displayedPatients.map((p) => {
              const matches = matchingSessionsByPatient[p.patient_id] || [];
              return (
                <div key={p.patient_id} className="px-5 py-4 hover:bg-pine-50/30 transition group">
                  <div className="flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-pine-50 text-pine flex items-center justify-center font-heading text-lg">
                      {p.first_name?.[0]}{p.last_name?.[0]}
                    </div>
                    <Link to={`/dashboard/patients/${p.patient_id}`} className="flex-1 min-w-0" data-testid={`patient-link-${p.patient_id}`}>
                      <div className="font-medium truncate">{p.first_name} {p.last_name}</div>
                      <div className="text-sm text-ink-muted truncate">{p.diagnosis || "—"}</div>
                    </Link>
                    {matches.length > 0 && (
                      <div className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-pine-50 text-pine text-xs font-semibold">
                        <FileText className="w-3 h-3" /> {matches.length}
                      </div>
                    )}
                    <button onClick={() => startEdit(p)} className="p-2 rounded-lg hover:bg-pine-50 opacity-0 group-hover:opacity-100 transition" data-testid={`edit-${p.patient_id}`}>
                      <Edit2 className="w-4 h-4 text-ink-muted" />
                    </button>
                    <button onClick={() => remove(p)} className="p-2 rounded-lg hover:bg-terracotta/10 opacity-0 group-hover:opacity-100 transition" data-testid={`delete-${p.patient_id}`}>
                      <Trash2 className="w-4 h-4 text-terracotta" />
                    </button>
                  </div>

                  {/* Matching sessions when search active */}
                  {matches.length > 0 && (
                    <div className="mt-3 ml-15 pl-14 border-l-2 border-pine-50 space-y-1">
                      {matches.slice(0, 3).map((s) => (
                        <Link
                          key={s.session_id}
                          to={`/dashboard/patients/${p.patient_id}?session=${s.session_id}`}
                          className="block py-1.5 px-3 rounded-lg hover:bg-pine-50/50 text-xs"
                          data-testid={`match-session-${s.session_id}`}
                        >
                          <span className="text-ink-muted font-mono">{new Date(s.created_at).toLocaleDateString()}</span>
                          <span className="ml-2 text-ink line-clamp-1 inline">{s.soap?.assessment || s.raw_notes?.slice(0, 100)}</span>
                        </Link>
                      ))}
                      {matches.length > 3 && (
                        <div className="text-xs text-ink-muted px-3">+{matches.length - 3}</div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Orphan sessions whose patient was filtered out (search-only no patient match) */}
      {filtersActive && (results.sessions || []).length > 0 && results.patients && results.patients.length === 0 && (
        <div className="bg-surface border border-hairline rounded-2xl p-5">
          <h3 className="font-heading text-lg mb-3">{t.patients.matchingSessions}</h3>
          <div className="space-y-1">
            {(results.sessions || []).slice(0, 10).map((s) => (
              <Link
                key={s.session_id}
                to={`/dashboard/patients/${s.patient_id}?session=${s.session_id}`}
                className="block px-3 py-2 rounded-lg hover:bg-pine-50/50 text-sm"
              >
                <span className="font-medium">{patientName(s.patient_id)}</span>
                <span className="text-ink-muted ml-2 text-xs">{new Date(s.created_at).toLocaleDateString()}</span>
                <div className="text-xs text-ink-muted line-clamp-1 mt-0.5">{s.soap?.assessment}</div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-ink/40 backdrop-blur-sm flex items-center justify-center p-4 z-50"
            onClick={() => setShowForm(false)}
          >
            <motion.form
              initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 10, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              onSubmit={submit}
              className="bg-surface rounded-3xl p-7 w-full max-w-2xl shadow-floating max-h-[90vh] overflow-y-auto"
              data-testid="patient-form"
            >
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-2xl">{editing ? t.patients.edit : t.patients.add}</h2>
                <button type="button" onClick={() => setShowForm(false)} className="p-2 rounded-lg hover:bg-pine-50"><X className="w-5 h-5" /></button>
              </div>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label={t.patients.first} value={form.first_name} onChange={(v) => setForm({ ...form, first_name: v })} required testid="first-name" />
                <Field label={t.patients.last} value={form.last_name} onChange={(v) => setForm({ ...form, last_name: v })} required testid="last-name" />
                <Field label={t.patients.dob} value={form.date_of_birth} onChange={(v) => setForm({ ...form, date_of_birth: v })} placeholder="1985-04-12" testid="dob" />
                <Field label={t.patients.gender} value={form.gender} onChange={(v) => setForm({ ...form, gender: v })} testid="gender" />
                <Field label={t.patients.phone} value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="phone" />
                <Field label={t.patients.email} value={form.email} onChange={(v) => setForm({ ...form, email: v })} type="email" testid="email" />
                <div className="sm:col-span-2"><Field label={t.patients.diagnosis} value={form.diagnosis} onChange={(v) => setForm({ ...form, diagnosis: v })} testid="diagnosis" /></div>
                <div className="sm:col-span-2">
                  <label className="text-xs uppercase tracking-[0.18em] font-semibold text-ink-muted">{t.patients.notes}</label>
                  <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3}
                    className="mt-2 w-full px-3 py-2 rounded-xl bg-bone border border-hairline focus:border-pine focus:outline-none"
                    data-testid="patient-notes" />
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button type="button" onClick={() => setShowForm(false)} className="px-5 py-2.5 rounded-full border border-hairline text-ink hover:bg-pine-50 transition">{t.patients.cancel}</button>
                <button type="submit" disabled={saving} className="px-5 py-2.5 rounded-full bg-pine text-bone hover:bg-pine-hover disabled:opacity-60 transition" data-testid="patient-save-btn">
                  {saving ? "..." : t.patients.save}
                </button>
              </div>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, placeholder, testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.18em] font-semibold text-ink-muted">{label}{required && " *"}</label>
      <input
        type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} placeholder={placeholder}
        className="mt-2 w-full px-3 py-2 rounded-xl bg-bone border border-hairline focus:border-pine focus:outline-none"
        data-testid={`field-${testid}`}
      />
    </div>
  );
}
